import {
  ChatbotAccessRightRequest,
  ChatbotAccessRightState,
  CirclesInviteTokenInfo,
  EmailVerification,
  SetPasswordTokenData,
  SupportAccessRightRequest,
  SupportAccessRightState,
} from '../../common/types';
import { getCookie, sleep } from '../../common/utils';
import {
  toChatbotAccessRightRequest,
  toCirclesInviteTokenInfo,
  toSetPasswordTokenData,
  toSupportAccessRightRequest,
} from '../converters/unauthenticated';
import {
  ApiChatbotAccessRightRequest,
  ApiCirclesInviteTokenInfo,
  ApiEmailVerification,
  ApiSetPasswordTokenData,
  ApiSupportAccessRightRequest,
  PostEmailVerificationPayload,
  PostResendSetPasswordEmailPayload,
  PostResendSetPasswordEmailResponse,
  PostResetPasswordPayload,
  PostSetPasswordPayload,
  PostSetPasswordResponse,
  PutChatbotAccessRightRequestPayload,
  PutChatbotAccessRightRequestResponse,
  PutSupportAccessRightRequestPayload,
  PutSupportAccessRightRequestResponse,
} from '../types';
import { getAPIUrl } from '../urls';
import { doFetch } from './index';

export function fetchSetPasswordTokenData(token?: string) {
  const uri = getAPIUrl(`account/setup-password?token=${token}`);

  return doFetch<ApiSetPasswordTokenData, SetPasswordTokenData>({
    method: 'GET',
    uri,
    converter: toSetPasswordTokenData,
  });
}

interface SetPasswordPayload
  extends Pick<
    SetPasswordTokenData,
    'termsOfServiceVersion' | 'privacyPolicyVersion'
  > {
  token: string;
  password: string;
  passwordConfirmation: string;
  acceptTOS: boolean;
  acceptMarketing: boolean;
}

export function postSetPassword({
  token,
  password,
  passwordConfirmation,
  privacyPolicyVersion,
  termsOfServiceVersion,
  acceptMarketing,
  acceptTOS,
}: SetPasswordPayload) {
  const uri = getAPIUrl('account/setup-password');
  return doFetch<
    PostSetPasswordResponse,
    { email: string },
    PostSetPasswordPayload
  >({
    method: 'POST',
    uri,
    converter: (data) => data,
    payload: {
      token,
      password,
      password_confirmation: passwordConfirmation,
      pp_version: privacyPolicyVersion,
      tos_version: termsOfServiceVersion,
      accept_health_data_processing: acceptTOS,
      accept_marketing: acceptMarketing,
    },
  });
}

export function postResendSetPasswordEmail(payload: {
  token: string;
  locale: string;
}) {
  const uri = getAPIUrl('account/resend-setup-password');
  return doFetch<
    PostResendSetPasswordEmailResponse,
    { email: string },
    PostResendSetPasswordEmailPayload
  >({
    method: 'POST',
    uri,
    converter: (data) => data,
    payload,
  });
}

async function fetchXsrfToken() {
  await doFetch({ method: 'GET', uri: '/api/xsrf', converter: (v) => v });
  await sleep(100);
  const token = getCookie('_xsrf');
  if (!token) {
    throw new Error('No xsrf cookie found');
  }
  return token;
}

export async function postResetPassword({
  token,
  password,
  passwordConfirmation,
}: {
  token: string;
  password: string;
  passwordConfirmation: string;
}) {
  const uri = getAPIUrl('account/reset-password');
  const xsrfToken = await fetchXsrfToken();
  return doFetch<unknown, 'OK', PostResetPasswordPayload>({
    uri,
    method: 'POST',
    customHeaders: [['X-Xsrftoken', xsrfToken]],
    payload: {
      token,
      password,
      password_confirmation: passwordConfirmation,
    },
    converter: () => 'OK',
  });
}

export function fetchSupportAccessRightRequest(token?: string) {
  const uri = getAPIUrl(`account/support-access?token=${token}`);

  return doFetch<ApiSupportAccessRightRequest, SupportAccessRightRequest>({
    method: 'GET',
    uri,
    converter: toSupportAccessRightRequest,
  });
}

interface SupportAccessRightRequestPayload {
  token: string;
  acceptAccess: boolean;
}

interface ChatbotAccessRightRequestPayload {
  token: string;
  acceptAccess: boolean;
}

export function putSupportAccessRightRequest({
  token,
  acceptAccess,
}: SupportAccessRightRequestPayload) {
  const uri = getAPIUrl('account/support-access');
  return doFetch<
    PutSupportAccessRightRequestResponse,
    { state: SupportAccessRightState },
    PutSupportAccessRightRequestPayload
  >({
    method: 'PUT',
    uri,
    converter: ({ state }) => ({ state }),
    payload: {
      token,
      accept_support_access: acceptAccess,
    },
  });
}

export function postVerifyEmail(token: string) {
  const uri = getAPIUrl('account/verify-email');

  return doFetch<
    ApiEmailVerification,
    EmailVerification,
    PostEmailVerificationPayload
  >({
    method: 'POST',
    uri,
    converter: (data: ApiEmailVerification): EmailVerification => data,
    payload: {
      token,
    },
  });
}

export function fetchChatbotAccessRightRequest(token?: string) {
  const uri = getAPIUrl(`account/chatbot-access?token=${token}`);

  return doFetch<ApiChatbotAccessRightRequest, ChatbotAccessRightRequest>({
    method: 'GET',
    uri,
    converter: toChatbotAccessRightRequest,
  });
}

export function putChatbotAccessRightRequest({
  token,
  acceptAccess,
}: ChatbotAccessRightRequestPayload) {
  const uri = getAPIUrl('account/chatbot-access');
  return doFetch<
    PutChatbotAccessRightRequestResponse,
    { state: ChatbotAccessRightState },
    PutChatbotAccessRightRequestPayload
  >({
    method: 'PUT',
    uri,
    converter: ({ state }) => ({ state }),
    payload: {
      token,
      accept_chatbot_session: acceptAccess,
    },
  });
}

export function fetchCirclesInviteTokenInfo(token?: string, campaign?: string) {
  let uri = getAPIUrl(`circles/invite/raf?invitation_token=${token}`);
  if (campaign) {
    uri += `&campaign=${campaign}`;
  }

  return doFetch<ApiCirclesInviteTokenInfo, CirclesInviteTokenInfo>({
    method: 'GET',
    uri,
    converter: toCirclesInviteTokenInfo,
  });
}

export async function fetchMoiJwtCookie({
  code,
  state,
  redirectUri,
}: {
  code: string;
  state: string;
  redirectUri: string;
}) {
  const uri = getAPIUrl('auth-cookie');
  const body = new URLSearchParams({
    authorization_code: code,
    state,
    redirect_uri: redirectUri,
  });

  return fetch(uri, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  }).then(() =>
    // Only successful response is status code 204.
    // Returning an empty object {} mirrors the behavior of doFetch for 204.
    ({}),
  );
}
